import { useEffect, useState } from 'react';
import { accidentAPI, vehicleAPI, driverAPI, garageAPI, companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Button, Table, Modal, Input, Select, Loading, Alert } from '../components/ui';
import { formatRs } from '../utils/currency';

const refNo = (id) => `ACC-${String(id).padStart(4, '0')}`;
const fmtDT = (v) => v ? new Date(v).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

const STATUS_META = {
  draft:            { label: 'Draft — not submitted', cls: 'bg-gray-100 text-gray-600' },
  pending_approval: { label: 'Pending Foreman Approval', cls: 'bg-yellow-100 text-yellow-800' },
  pending_manager:  { label: 'Pending Manager Sign-off', cls: 'bg-blue-100 text-blue-700' },
  completed:        { label: 'Completed', cls: 'bg-green-100 text-green-700' },
  rejected:         { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
};

const emptyForm = {
  vehicle_id:'', driver_id:'', owner:'', insurance_provider:'', policy_no:'',
  accident_date: new Date().toISOString().split('T')[0], accident_time:'', place:'',
  damage_details:'', description:'',
  driver_name:'', driver_designation:'', driver_licence_no:'', driver_nic:'', driver_contact:'',
  third_party:false, third_party_desc:'', third_party_contact:'',
  quoted_amount:'', approved_amount:'', garage_id:'',
  claim_payment_method:'', claim_cheque_no:'', claim_cheque_bank:'', claim_cheque_date:'',
  balance_amount:'', balance_payer_type:'', balance_payer_name:'', balance_payer_contact:'',
  balance_method:'', balance_cheque_no:'', balance_cheque_bank:'', balance_cheque_date:'',
};

// ── Printable accident / insurance claim form ────────────────────────────────
const printClaim = (a) => {
  const existing = document.getElementById('acc-print-frame');
  if (existing) existing.remove();
  const iframe = document.createElement('iframe');
  iframe.id = 'acc-print-frame';
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const rs = (v) => v == null || v === '' ? '—' : 'Rs. ' + Number(v).toLocaleString('en-LK', { minimumFractionDigits: 2 });
  const r = (l, v) => `<tr><th>${l}</th><td>${v ?? '—'}</td></tr>`;
  const v = a.vehicle || {};
  const html = `
    <h2>Motor Accident / Insurance Claim Form</h2>
    <div class="sub">Reference: ${refNo(a.id)}</div>

    <div class="sec">Vehicle &amp; Policy</div>
    <table>
      ${r('Vehicle No.', v.reg_number)}
      ${r('Make', v.brand)}
      ${r('Model', v.model)}
      ${r('Owner', a.owner)}
      ${r('Insurance Provider', a.insurance_provider)}
      ${r('Policy No.', a.policy_no)}
    </table>

    <div class="sec">Accident Details</div>
    <table>
      ${r('Date of Accident', a.accident_date)}
      ${r('Time', a.accident_time)}
      ${r('Place', a.place)}
      ${r('Details of Damages', a.damage_details)}
      ${r('Description of the Accident', a.description)}
    </table>

    <div class="sec">Driver's Details</div>
    <table>
      ${r('Name', a.driver_name)}
      ${r('Designation', a.driver_designation)}
      ${r('Licence No.', a.driver_licence_no)}
      ${r('NIC', a.driver_nic)}
      ${r('Contact No.', a.driver_contact)}
    </table>

    ${a.third_party ? `
    <div class="sec">Third Party Involved</div>
    <table>
      ${r('Description', a.third_party_desc)}
      ${r('Contact No.', a.third_party_contact)}
    </table>` : ''}

    <div class="sec">Claim Details</div>
    <table>
      ${r('Quoted Amount', rs(a.quoted_amount))}
      ${r('Approved Amount by Insurance', rs(a.approved_amount))}
      ${r('Repair Done By (Garage)', a.garage?.name)}
      ${r('Claim Received — Amount', rs(a.claim_amount))}
      ${r('Paid By', a.claim_payment_method === 'cash' ? 'Cash' : a.claim_payment_method ? 'Cheque' : null)}
      ${r('Cheque No', a.claim_cheque_no)}
      ${r('Cheque Date', a.claim_cheque_date)}
      ${r("Cheque's Bank", a.claim_cheque_bank)}
      ${Array.isArray(a.balance_payments) && a.balance_payments.length ? `
        <tr><th>Balance Split</th><td>${a.balance_payments.map(p =>
          `${p.payer_type}${p.payer_name ? ' (' + p.payer_name + ')' : ''}: ${rs(p.amount)}${p.share_pct ? ' — ' + p.share_pct + '%' : ''}${p.method ? ' via ' + (p.method === 'cash' ? 'cash' : 'cheque ' + (p.cheque_no || '')) : ''}`
        ).join('<br/>')}</td></tr>
      ` : ''}
      ${a.balance_amount ? `
        ${r('Balance Amount', rs(a.balance_amount))}
        ${r('Balance Paid By', [a.balance_payer_type, a.balance_payer_name].filter(Boolean).join(' — '))}
        ${r('Balance Contact', a.balance_payer_contact)}
        ${r('Balance Method', a.balance_method === 'cash' ? 'Cash' : a.balance_method ? 'Cheque' : null)}
        ${r('Balance Cheque No', a.balance_cheque_no)}
        ${r('Balance Cheque Date', a.balance_cheque_date)}
        ${r("Balance Cheque's Bank", a.balance_cheque_bank)}
      ` : ''}
    </table>

    <table class="sign">
      <tr>
        <td><div class="line">Reported By${a.reporter?.name ? '<br/>' + a.reporter.name : ''}</div></td>
        <td><div class="line">Mechanical Foreman${a.reviewer?.name ? '<br/>' + a.reviewer.name : ''}</div></td>
        <td><div class="line">Head of Institute</div></td>
      </tr>
    </table>`;
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`<html><head><title>Accident Claim ${refNo(a.id)}</title><style>
    body{font-family:Arial,sans-serif;font-size:13px;padding:45px;max-width:820px;margin:0 auto;}
    h2{text-align:center;text-transform:uppercase;letter-spacing:1px;margin:0 0 4px;}
    .sub{text-align:center;color:#555;margin-bottom:20px;}
    table{width:100%;border-collapse:collapse;margin-bottom:14px;}
    th{background:#f2f2f2;text-align:left;padding:6px 10px;border:1px solid #ccc;width:36%;}
    td{padding:6px 10px;border:1px solid #ccc;}
    .sec{font-weight:bold;background:#e8e4f3;padding:6px 10px;border:1px solid #ccc;}
    .sign td{border:0;padding-top:52px;}
    .line{border-top:1px solid #333;width:210px;padding-top:4px;font-size:12px;}
    @media print { @page { margin: 1.8cm; } }
  </style></head><body>${html}</body></html>`);
  doc.close();
  setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); }, 400);
};

export default function Accidents() {
  const { can } = useAuth();
  const [items,    setItems]    = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers,  setDrivers]  = useState([]);
  const [garages,  setGarages]  = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [filter,   setFilter]   = useState('');
  const [saving,   setSaving]   = useState(false);

  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState(emptyForm);
  const [payers,  setPayers]  = useState([]);   // balance split across payers

  const balanceDue = (Number(form.quoted_amount) || 0) - (Number(form.approved_amount) || 0);
  const payersTotal = payers.reduce((t, p) => t + (Number(p.amount) || 0), 0);
  const setPayer = (i, patch) => setPayers(ps => ps.map((p, k) => k === i ? { ...p, ...patch } : p));
  const addPayer = () => setPayers(ps => [...ps, {
    payer_type:'company', payer_name:'', payer_contact:'', share_pct:'', amount:'',
    method:'', cheque_no:'', cheque_bank:'', cheque_date:'',
  }]);
  // typing a % fills the amount from the balance due
  const setPayerPct = (i, pct) => {
    const amt = balanceDue > 0 && pct ? ((balanceDue * Number(pct)) / 100).toFixed(2) : '';
    setPayer(i, { share_pct: pct, amount: amt });
  };

  const [viewing, setViewing] = useState(null);


  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 3500); };

  const load = () => {
    setLoading(true);
    Promise.all([
      accidentAPI.getAll(),
      vehicleAPI.getAll({ limit: 200 }),
      driverAPI.getAll({ limit: 200 }),
      garageAPI.getAll().catch(() => ({ data: { data: [] } })),
      companyAPI.getAll().catch(() => ({ data: { data: [] } })),
    ])
      .then(([a, v, d, g, c]) => {
        setCompanies(c.data.data || []);
        setItems(a.data.data || []);
        setVehicles(v.data.data || []);
        setDrivers(d.data.data || []);
        setGarages(g.data.data || []);
      })
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openAdd = () => {
    setEditing(null); setPayers([]);
    setForm({ ...emptyForm, accident_date: new Date().toISOString().split('T')[0] });
    setModal(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setPayers((a.balance_payments || []).map(p => ({ ...p })));
    setForm({ ...emptyForm, ...a, third_party: !!a.third_party,
      accident_time: a.accident_time || '', garage_id: a.garage_id || '', driver_id: a.driver_id || '' });
    setModal(true);
  };

  // Vehicle pick → prefill owner/insurer/policy
  const onVehicle = (e) => {
    const id = e.target.value;
    const v = vehicles.find(x => String(x.id) === id);
    setForm(f => ({ ...f, vehicle_id: id,
      owner: v?.company?.name || companies.find(c => c.id === v?.company_id)?.name || f.owner,
      insurance_provider: f.insurance_provider || v?.insurance_provider || '',
      policy_no: f.policy_no || v?.insurance_no || '' }));
  };
  // Driver pick → prefill driver detail block
  const onDriver = (e) => {
    const id = e.target.value;
    const d = drivers.find(x => String(x.id) === id);
    setForm(f => ({ ...f, driver_id: id,
      driver_name: d?.user?.name || f.driver_name,
      driver_licence_no: d?.license_number || f.driver_licence_no,
      driver_contact: d?.user?.phone || d?.alt_mobile || f.driver_contact,
      driver_designation: d?.user?.designation || f.driver_designation,
      driver_nic: d?.user?.nic || f.driver_nic }));
  };

  const save = async (e, mode = 'submit') => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, balance_payments: payers, ...(mode === 'draft' ? { save_draft: true } : { submit: true }) };
      if (editing) {
        await accidentAPI.update(editing.id, payload);
        flash(mode === 'draft' ? 'Saved as draft' :
          editing.status === 'rejected' ? 'Re-submitted for foreman review' : 'Accident record updated');
      } else {
        await accidentAPI.create(payload);
        flash(mode === 'draft' ? 'Saved as draft — submit when ready' : 'Accident reported — forwarded to foreman');
      }
      setModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };


  const del = async (a) => {
    if (!window.confirm(`Delete accident record ${refNo(a.id)}?`)) return;
    try { await accidentAPI.remove(a.id); flash('Record deleted'); load(); }
    catch (err) { setError(err.response?.data?.message || 'Delete failed'); }
  };

  const visible = items.filter(a => !filter || a.status === filter);

  return (
    <div className="space-y-4">
      <PageHeader title="Accidents & Insurance Claims"
        subtitle={`${items.length} record${items.length === 1 ? '' : 's'}`}
        action={can('admin','transport_officer','manager') && <Button onClick={openAdd}>+ Report Accident</Button>} />
      {error   && <Alert type="error"   onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess('')}>{success}</Alert>}

      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilter('')}
          className={`px-3 py-1.5 rounded-full text-sm border ${!filter ? 'bg-primary-600 text-white border-primary-600' : 'bg-white'}`}>
          All ({items.length})
        </button>
        {Object.entries(STATUS_META).map(([k, m]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-sm border ${filter === k ? 'bg-primary-600 text-white border-primary-600' : 'bg-white'}`}>
            {m.label} ({items.filter(a => a.status === k).length})
          </button>
        ))}
      </div>

      {loading ? <Loading /> : (
        <Table headers={['Ref / Vehicle','Date & Place','Driver','Claim','Status','Actions']} empty="No accident records">
          {visible.map(a => (
            <tr key={a.id} className="border-b last:border-0 hover:bg-gray-50">
              <td className="px-4 py-3">
                <p className="font-mono text-sm font-semibold text-primary-700">{refNo(a.id)}</p>
                <p className="text-xs text-gray-500">{a.vehicle?.reg_number} — {a.vehicle?.brand} {a.vehicle?.model}</p>
                <p className="text-xs text-gray-400">by {a.reporter?.name}</p>
              </td>
              <td className="px-4 py-3 text-sm">
                {a.accident_date}{a.accident_time ? ` ${a.accident_time}` : ''}
                <p className="text-xs text-gray-400 max-w-[160px] truncate" title={a.place}>{a.place || '—'}</p>
              </td>
              <td className="px-4 py-3 text-sm">
                {a.driver_name || '—'}
                {a.third_party ? <p className="text-xs text-orange-500">3rd party involved</p> : null}
              </td>
              <td className="px-4 py-3 text-sm">
                {a.quoted_amount ? <p>Quoted {formatRs(a.quoted_amount)}</p> : null}
                {a.approved_amount ? <p className="text-xs text-gray-500">Approved {formatRs(a.approved_amount)}</p> : null}
                {a.claim_amount ? <p className="text-xs text-green-700">Received {formatRs(a.claim_amount)}{a.claim_cheque_no ? ` · Chq ${a.claim_cheque_no}` : ''}</p> : null}
              </td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 rounded-full text-xs ${STATUS_META[a.status]?.cls || ''}`}>
                  {STATUS_META[a.status]?.label || a.status}
                </span>
                {a.status === 'rejected' && a.review_remark && (
                  <p className="text-xs text-red-500 mt-1 max-w-[200px]">✖ {a.review_remark}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">Reported {fmtDT(a.created_at)}</p>
              </td>
              <td className="px-4 py-3">
                <div className="flex gap-1 flex-wrap">
                  <Button variant="ghost" size="sm" title="View" onClick={() => setViewing(a)}>👁️</Button>
                  <Button variant="ghost" size="sm" title="Print claim form" onClick={() => printClaim(a)}>📄</Button>
                  {can('admin','foreman') && a.status === 'pending_approval' && (
                    <span className="text-xs text-primary-600 self-center" title="Review in the Approvals menu">→ Approvals</span>
                  )}
                  {can('admin','manager') && a.status === 'pending_manager' && (
                    <span className="text-xs text-primary-600 self-center" title="Sign off in the Approvals menu">→ Approvals</span>
                  )}
                  {a.status === 'pending_manager' && !can('admin','manager') && (
                    <span className="text-xs text-blue-600 self-center">👔 With manager</span>
                  )}
                  {a.status === 'completed' && (
                    <span className="text-xs text-green-700 self-center">✓ Completed — read only</span>
                  )}
                  {a.status === 'pending_approval' && (
                    <span className="text-xs text-gray-400 self-center">🔒 Awaiting foreman</span>
                  )}
                  {a.status === 'pending_manager' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 self-center">👔 Manager sign-off</span>
                  )}
                  {can('admin','manager') && a.status === 'pending_manager' && (
                    <span className="text-xs text-primary-600 self-center" title="Sign off in the Approvals menu">→ Approvals</span>
                  )}
                  {a.status === 'completed' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 self-center">✓ Completed</span>
                  )}
                  {can('admin','transport_officer','manager') && ['draft','rejected'].includes(a.status) && (
                    <Button variant="ghost" size="sm" title={a.status === 'rejected' ? 'Edit & re-submit' : 'Edit'} onClick={() => openEdit(a)}>✏️</Button>
                  )}
                  {can('admin','transport_officer') && ['draft','rejected'].includes(a.status) && (
                    <Button variant="ghost" size="sm" title="Delete" onClick={() => del(a)}>🗑️</Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}

      {/* ══ REPORT / EDIT ═════════════════════════════════════════════════════ */}
      <Modal open={modal} onClose={() => setModal(false)} size="xl"
        title={editing ? `Edit ${refNo(editing.id)}` : 'Report Accident'}>
        <form onSubmit={save} className="space-y-4">
          <div className="bg-gray-50 border rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">🚗 Vehicle &amp; Policy</p>
            <div className="grid grid-cols-2 gap-4">
              <Select label="Vehicle *" value={form.vehicle_id} onChange={onVehicle} required>
                <option value="">Select vehicle…</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.reg_number} — {v.brand} {v.model}</option>)}
              </Select>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Owner (from vehicle book)</label>
                <input list="acc-owner-list" value={form.owner || ''}
                  onChange={e => setForm({ ...form, owner: e.target.value })}
                  placeholder="auto-filled from the vehicle's owner company"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300" />
                <datalist id="acc-owner-list">
                  {companies.map(c => <option key={c.id} value={c.name} />)}
                </datalist>
              </div>
              <Input label="Insurance Provider" value={form.insurance_provider}
                onChange={e => setForm({ ...form, insurance_provider: e.target.value })} placeholder="e.g. SLIC, Ceylinco" />
              <Input label="Policy No." value={form.policy_no}
                onChange={e => setForm({ ...form, policy_no: e.target.value })} />
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-3">💥 Accident Details</p>
            <div className="grid grid-cols-3 gap-4">
              <Input label="Date of Accident *" type="date" required value={form.accident_date}
                onChange={e => setForm({ ...form, accident_date: e.target.value })} />
              <Input label="Time" type="time" value={form.accident_time}
                onChange={e => setForm({ ...form, accident_time: e.target.value })} />
              <Input label="Place" value={form.place}
                onChange={e => setForm({ ...form, place: e.target.value })} placeholder="e.g. Galle Road, Colombo 03" />
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Details of Damages</label>
              <textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows="2" value={form.damage_details}
                onChange={e => setForm({ ...form, damage_details: e.target.value })} />
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description of the Accident</label>
              <textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows="3" value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">👤 Driver's Details</p>
            <Select label="Select Driver (auto-fills below)" value={form.driver_id} onChange={onDriver}>
              <option value="">Not a registered driver / enter manually</option>
              {drivers.map(d => <option key={d.id} value={d.id}>{d.user?.name} — {d.license_number}</option>)}
            </Select>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <Input label="Name" value={form.driver_name}
                onChange={e => setForm({ ...form, driver_name: e.target.value })} />
              <Input label="Designation" value={form.driver_designation}
                onChange={e => setForm({ ...form, driver_designation: e.target.value })} />
              <Input label="Licence No." value={form.driver_licence_no}
                onChange={e => setForm({ ...form, driver_licence_no: e.target.value })} />
              <Input label="NIC" value={form.driver_nic}
                onChange={e => setForm({ ...form, driver_nic: e.target.value })} />
              <Input label="Contact No." value={form.driver_contact}
                onChange={e => setForm({ ...form, driver_contact: e.target.value })} />
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <label className="flex items-center gap-2 text-sm font-medium text-orange-800">
              <input type="checkbox" checked={form.third_party}
                onChange={e => setForm({ ...form, third_party: e.target.checked })} />
              Third party involved
            </label>
            {form.third_party && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows="2" value={form.third_party_desc}
                    onChange={e => setForm({ ...form, third_party_desc: e.target.value })}
                    placeholder="Third party vehicle / person details" />
                </div>
                <Input label="Contact No." value={form.third_party_contact}
                  onChange={e => setForm({ ...form, third_party_contact: e.target.value })} />
              </div>
            )}
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-3">💰 Claim Details</p>
            <div className="grid grid-cols-3 gap-4">
              <Input label="Full Amount (Rs.)" type="number" step="0.01" value={form.quoted_amount}
                onChange={e => setForm({ ...form, quoted_amount: e.target.value })}
                placeholder="total repair cost" />
              <Input label="Approved Amount from Insurance (Rs.)" type="number" step="0.01" value={form.approved_amount}
                onChange={e => setForm({ ...form, approved_amount: e.target.value })} />
              <Select label="Repair Done By (Garage)" value={form.garage_id}
                onChange={e => setForm({ ...form, garage_id: e.target.value })}>
                <option value="">Select garage…</option>
                {garages.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </Select>
            </div>

            {/* How the insurance amount is paid */}
            <div className="grid grid-cols-3 gap-4 mt-4 pt-3 border-t border-green-200">
              <Select label="Insurance Paid By" value={form.claim_payment_method || ''}
                onChange={e => setForm({ ...form, claim_payment_method: e.target.value })}>
                <option value="">Not received yet</option>
                <option value="cheque">Cheque</option>
                <option value="cash">Cash</option>
              </Select>
              {form.claim_payment_method === 'cheque' && (
                <>
                  <Input label="Cheque No." value={form.claim_cheque_no || ''}
                    onChange={e => setForm({ ...form, claim_cheque_no: e.target.value })} />
                  <Input label="Cheque Date" type="date" value={form.claim_cheque_date || ''}
                    onChange={e => setForm({ ...form, claim_cheque_date: e.target.value })} />
                  <div className="col-span-3">
                    <Input label="Cheque's Bank" value={form.claim_cheque_bank || ''}
                      onChange={e => setForm({ ...form, claim_cheque_bank: e.target.value })}
                      placeholder="e.g. BOC — Colombo Main" />
                  </div>
                </>
              )}
            </div>

            {/* Balance — split across one or more payers */}
            {balanceDue > 0 && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                    ⚖️ Balance to Pay — Rs. {balanceDue.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                  </p>
                  <button type="button" onClick={addPayer} className="text-xs text-primary-600 hover:underline">
                    + Add payer
                  </button>
                </div>

                {payers.length === 0 && (
                  <p className="text-xs text-gray-500">No payers added — click “+ Add payer” to split this balance
                    (e.g. Driver 30%, Company 70%).</p>
                )}

                <div className="space-y-3">
                  {payers.map((p, i) => (
                    <div key={i} className="bg-white rounded-lg border border-amber-200 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-gray-500">Payer #{i + 1}</span>
                        <button type="button" className="text-xs text-red-500 hover:underline"
                          onClick={() => setPayers(ps => ps.filter((_, k) => k !== i))}>remove</button>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <Select label="Payer" value={p.payer_type || 'company'}
                          onChange={e => setPayer(i, { payer_type: e.target.value })}>
                          <option value="company">Company</option>
                          <option value="driver">Driver</option>
                          <option value="officer">Officer</option>
                          <option value="third_party">Third Party</option>
                          <option value="insurance">Insurance</option>
                          <option value="other">Other</option>
                        </Select>
                        <Input label="Name" value={p.payer_name || ''}
                          onChange={e => setPayer(i, { payer_name: e.target.value })} />
                        <Input label="Share %" type="number" step="0.01" value={p.share_pct || ''}
                          onChange={e => setPayerPct(i, e.target.value)} placeholder="e.g. 30" />
                        <Input label="Amount (Rs.)" type="number" step="0.01" value={p.amount || ''}
                          onChange={e => setPayer(i, { amount: e.target.value })} />
                        <Input label="Contact No." value={p.payer_contact || ''}
                          onChange={e => setPayer(i, { payer_contact: e.target.value })} />
                        <Select label="Paid By" value={p.method || ''}
                          onChange={e => setPayer(i, { method: e.target.value })}>
                          <option value="">Not paid yet</option>
                          <option value="cheque">Cheque</option>
                          <option value="cash">Cash</option>
                        </Select>
                        {p.method === 'cheque' && (
                          <>
                            <Input label="Cheque No." value={p.cheque_no || ''}
                              onChange={e => setPayer(i, { cheque_no: e.target.value })} />
                            <Input label="Cheque Date" type="date" value={p.cheque_date || ''}
                              onChange={e => setPayer(i, { cheque_date: e.target.value })} />
                            <div className="col-span-2">
                              <Input label="Cheque's Bank" value={p.cheque_bank || ''}
                                onChange={e => setPayer(i, { cheque_bank: e.target.value })} />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {payers.length > 0 && (
                  <div className={`mt-3 text-xs font-medium ${
                    Math.abs(payersTotal - balanceDue) < 0.01 ? 'text-green-700' : 'text-red-600'}`}>
                    Allocated: Rs. {payersTotal.toLocaleString('en-LK', { minimumFractionDigits: 2 })} of
                    Rs. {balanceDue.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                    {Math.abs(payersTotal - balanceDue) < 0.01 ? ' ✓ fully allocated'
                      : ` · ${payersTotal > balanceDue ? 'over by' : 'short by'} Rs. ${Math.abs(balanceDue - payersTotal).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            {(!editing || ['draft','rejected'].includes(editing.status)) && (
              <Button type="button" variant="ghost" disabled={saving}
                onClick={(ev) => save(ev, 'draft')}
                title="Save without sending to the foreman — you can edit it later">
                💾 Save Draft
              </Button>
            )}
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : editing
                ? (['draft','rejected'].includes(editing.status) ? 'Submit to Foreman' : 'Save Changes')
                : 'Submit to Foreman'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ══ VIEW ══════════════════════════════════════════════════════════════ */}
      <Modal open={!!viewing} onClose={() => setViewing(null)} size="xl"
        title={`${viewing ? refNo(viewing.id) : ''} — Accident Details`}>
        {viewing && (
          <div className="space-y-2 text-sm">
            {[
              ['Vehicle', `${viewing.vehicle?.reg_number || ''} — ${viewing.vehicle?.brand || ''} ${viewing.vehicle?.model || ''}`],
              ['Owner', viewing.owner], ['Insurance Provider', viewing.insurance_provider], ['Policy No.', viewing.policy_no],
              ['Date / Time', `${viewing.accident_date || ''} ${viewing.accident_time || ''}`], ['Place', viewing.place],
              ['Damages', viewing.damage_details], ['Description', viewing.description],
              ['Driver', viewing.driver_name], ['Designation', viewing.driver_designation],
              ['Licence No.', viewing.driver_licence_no], ['NIC', viewing.driver_nic], ['Contact', viewing.driver_contact],
              ...(viewing.third_party ? [['Third Party', viewing.third_party_desc], ['Third Party Contact', viewing.third_party_contact]] : []),
              ['Quoted Amount', viewing.quoted_amount ? formatRs(viewing.quoted_amount) : null],
              ['Approved Amount', viewing.approved_amount ? formatRs(viewing.approved_amount) : null],
              ['Repair Done By', viewing.garage?.name],
              ['Claim Received', viewing.claim_amount ? formatRs(viewing.claim_amount) : null],
              ['Cheque No', viewing.claim_cheque_no], ['Cheque Date', viewing.claim_cheque_date],
              ["Cheque's Bank", viewing.claim_cheque_bank],
              ['Balance Split', (viewing.balance_payments || []).length
                ? viewing.balance_payments.map(p =>
                    `${p.payer_type}${p.payer_name ? ' — ' + p.payer_name : ''}: ${formatRs(p.amount)}${p.share_pct ? ` (${p.share_pct}%)` : ''}`
                  ).join(' · ')
                : null],
              ['Balance Amount', viewing.balance_amount ? formatRs(viewing.balance_amount) : null],
              ['Balance Paid By', [viewing.balance_payer_type, viewing.balance_payer_name].filter(Boolean).join(' — ') || null],
              ['Balance Contact', viewing.balance_payer_contact],
              ['Balance Cheque No', viewing.balance_cheque_no],
              ['Balance Cheque Date', viewing.balance_cheque_date],
              ['Reported By', `${viewing.reporter?.name || '—'} · ${fmtDT(viewing.created_at)}`],
              ...(viewing.reviewer ? [['Reviewed By', `${viewing.reviewer.name} · ${fmtDT(viewing.reviewed_at)}`]] : []),
              ['Review Remark', viewing.review_remark],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="flex border-b border-gray-100 py-1.5">
                <div className="w-48 shrink-0 font-medium text-gray-600">{k}</div>
                <div className="text-gray-800">{v}</div>
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-3">
              <Button variant="ghost" onClick={() => printClaim(viewing)}>📄 Print Claim Form</Button>
              <Button onClick={() => setViewing(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
